import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  CheckSquare2,
  MapPin,
  Phone,
  RotateCcw,
  UserRound,
} from "lucide-react";
import ReactSelect from "../../ReactSelect";

export const isStopLocked = (stop) => {
  const status = String(stop?.status || "").trim().toUpperCase().replace(/-/g, "_");
  const operational = ["REACHED", "ARRIVED", "IN_PROGRESS", "STARTED", "COMPLETED"].some(
    (prefix) => status === prefix || status.startsWith(`${prefix} `) || status.startsWith(`${prefix}_`),
  );
  return operational || String(stop?.sequence_lock || "NONE").toUpperCase() === "FIXED";
};

export const buildAssignmentPlan = (stops = [], fallbackVehicleId = "") => {
  const rows = new Map();
  stops
    .filter((stop) => Number(stop.booking_id) !== 1)
    .forEach((stop) => {
      const bookingId = Number(stop.booking_id);
      if (rows.has(bookingId)) return;
      const currentVehicleId = String(
        stop.assigned_vehicle_id || stop.source_vehicle_id || fallbackVehicleId || "",
      );
      rows.set(bookingId, {
        bookingId,
        currentVehicleId,
        targetVehicleId: currentVehicleId || null,
        assignmentVersion: Number(stop.assignment_version || 0),
        initialAssignmentLock: String(stop.assignment_lock || "VEHICLE").toUpperCase(),
        assignmentLock: String(stop.assignment_lock || "VEHICLE").toUpperCase(),
        currentDriverName: stop.driver_name || stop.source_driver_name || null,
        currentDriverMobile: stop.driver_mobile || stop.source_driver_mobile || null,
        locked:
          String(stop.assignment_lock || "NONE").toUpperCase() === "FULL" ||
          stops.some(
            (candidate) =>
              Number(candidate.booking_id) === bookingId && isStopLocked(candidate),
          ),
      });
    });
  return Array.from(rows.values()).sort((left, right) => left.bookingId - right.bookingId);
};

export const validateStopOrder = (stops = []) => {
  const positions = new Map();
  stops.forEach((stop, index) => positions.set(String(stop.stop_id), index));
  const bookingPositions = new Map();
  stops.forEach((stop) => {
    const bookingId = Number(stop.booking_id);
    if (!Number.isInteger(bookingId) || bookingId === 1) return;
    const entry = bookingPositions.get(bookingId) || {};
    entry[String(stop.type || "").toLowerCase()] = positions.get(String(stop.stop_id));
    bookingPositions.set(bookingId, entry);
  });
  for (const [bookingId, entry] of bookingPositions.entries()) {
    if (entry.pickup != null && entry.drop != null && entry.pickup > entry.drop) {
      return `Booking #${bookingId} has its drop before its pickup.`;
    }
  }
  return "";
};

const driverLabel = (driver) =>
  `${driver?.driver_name || "Unnamed driver"} · ${driver?.mobile_number || "No phone number"}`;

const StopAssignmentPlanner = ({
  stops,
  setStops,
  assignments,
  setAssignments,
  drivers,
  sequenceLock,
  setSequenceLock,
  disabled = false,
  onError,
}) => {
  const [selectedStopIds, setSelectedStopIds] = useState([]);
  const [batchMode, setBatchMode] = useState("KEEP");
  const [batchVehicleId, setBatchVehicleId] = useState("");
  const [batchAssignmentLock, setBatchAssignmentLock] = useState("VEHICLE");

  useEffect(() => {
    setSelectedStopIds((current) =>
      current.filter((stopId) => stops.some((stop) => String(stop.stop_id) === String(stopId))),
    );
  }, [stops]);

  const driverOptions = useMemo(
    () => drivers.map((driver) => ({
      value: String(driver.vehicle_id),
      label: driverLabel(driver),
      searchText: `${driver.driver_name || ""} ${driver.mobile_number || ""}`,
      driver,
    })),
    [drivers],
  );
  const driverByVehicle = useMemo(
    () => new Map(drivers.map((driver) => [String(driver.vehicle_id), driver])),
    [drivers],
  );
  const assignmentByBooking = useMemo(
    () => new Map(assignments.map((row) => [Number(row.bookingId), row])),
    [assignments],
  );
  const selectedDriverOption = driverOptions.find(
    (option) => String(option.value) === String(batchVehicleId),
  ) || null;

  const toggleStop = (stop) => {
    if (disabled || isStopLocked(stop) || assignmentByBooking.get(Number(stop.booking_id))?.locked) return;
    const stopId = String(stop.stop_id);
    setSelectedStopIds((current) =>
      current.includes(stopId)
        ? current.filter((value) => value !== stopId)
        : [...current, stopId],
    );
  };

  const selectAllEditable = () => {
    const editable = stops
      .filter((stop) => !isStopLocked(stop) && !assignmentByBooking.get(Number(stop.booking_id))?.locked)
      .map((stop) => String(stop.stop_id));
    setSelectedStopIds(editable);
  };

  const applyBatch = () => {
    if (!selectedStopIds.length) {
      onError?.("Select at least one pickup or drop stop first.");
      return;
    }
    if (batchMode === "ASSIGN" && !batchVehicleId) {
      onError?.("Choose a driver before applying the assignment.");
      return;
    }
    const selectedBookingIds = new Set(
      stops
        .filter((stop) => selectedStopIds.includes(String(stop.stop_id)))
        .map((stop) => Number(stop.booking_id)),
    );
    setAssignments((current) => current.map((row) => {
      if (!selectedBookingIds.has(Number(row.bookingId)) || row.locked) return row;
      if (batchMode === "UNASSIGN") {
        return {
          ...row,
          targetVehicleId: null,
          assignmentLock: "NONE",
        };
      }
      if (batchMode === "KEEP") {
        return {
          ...row,
          targetVehicleId: row.currentVehicleId || null,
          assignmentLock: batchAssignmentLock,
        };
      }
      return {
        ...row,
        targetVehicleId: String(batchVehicleId),
        assignmentLock: batchAssignmentLock,
      };
    }));
    setSelectedStopIds([]);
    onError?.("");
  };

  const resetAssignments = () => {
    setAssignments((current) => current.map((row) => ({
      ...row,
      targetVehicleId: row.currentVehicleId || null,
      assignmentLock: row.initialAssignmentLock || "VEHICLE",
    })));
    setSelectedStopIds([]);
    onError?.("");
  };

  const moveStop = (index, direction) => {
    if (disabled) return;
    const target = index + direction;
    if (target < 0 || target >= stops.length) return;
    const current = stops[index];
    const neighbour = stops[target];
    if (
      isStopLocked(current) ||
      isStopLocked(neighbour) ||
      assignmentByBooking.get(Number(current.booking_id))?.locked ||
      assignmentByBooking.get(Number(neighbour.booking_id))?.locked
    ) {
      onError?.("Started, completed, fixed or fully locked stops cannot be moved.");
      return;
    }
    setStops((previous) => {
      const next = [...previous];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    onError?.("");
  };

  const plannedDriver = (assignment) => {
    if (!assignment?.targetVehicleId) return null;
    return driverByVehicle.get(String(assignment.targetVehicleId)) || {
      driver_name: assignment.currentDriverName,
      mobile_number: assignment.currentDriverMobile,
    };
  };

  return (
    <div className="hitl-stop-planner">
      <div className="hitl-planner-note">
        <strong>Sequence is controlled stop by stop.</strong>
        <span>
          Pickup and drop can be placed independently in the route. Assignment remains booking-level,
          so applying an action to either stop assigns or unassigns that booking&apos;s complete pickup/drop pair.
        </span>
      </div>

      <div className="hitl-planner-toolbar">
        <div className="hitl-selection-summary">
          <CheckSquare2 size={18} />
          <strong>{selectedStopIds.length}</strong> stop{selectedStopIds.length === 1 ? "" : "s"} selected
        </div>
        <button type="button" className="hitl-secondary-button" onClick={selectAllEditable} disabled={disabled}>
          Select editable stops
        </button>
        <button type="button" className="hitl-secondary-button" onClick={() => setSelectedStopIds([])} disabled={disabled}>
          Clear selection
        </button>
        <button type="button" className="hitl-secondary-button" onClick={resetAssignments} disabled={disabled}>
          <RotateCcw size={15} /> Reset assignments
        </button>
      </div>

      <div className="hitl-batch-panel">
        <div className="hitl-radio-row">
          <label>
            <input
              type="radio"
              name="hitl-batch-mode"
              checked={batchMode === "KEEP"}
              onChange={() => setBatchMode("KEEP")}
              disabled={disabled}
            />
            Keep with current driver
          </label>
          <label>
            <input
              type="radio"
              name="hitl-batch-mode"
              checked={batchMode === "ASSIGN"}
              onChange={() => setBatchMode("ASSIGN")}
              disabled={disabled}
            />
            Assign to another driver
          </label>
          <label>
            <input
              type="radio"
              name="hitl-batch-mode"
              checked={batchMode === "UNASSIGN"}
              onChange={() => setBatchMode("UNASSIGN")}
              disabled={disabled}
            />
            Don&apos;t assign — return booking to Trips
          </label>
        </div>

        <div className="hitl-batch-controls">
          {batchMode === "ASSIGN" && (
            <div className="hitl-control-grow">
              <label>Driver</label>
              <ReactSelect
                options={driverOptions}
                value={selectedDriverOption}
                handleSelectedOption={(option) => setBatchVehicleId(option?.value || "")}
                placeholder="Search driver name or phone"
                isDisabled={disabled}
              />
            </div>
          )}
          {batchMode !== "UNASSIGN" && (
            <div className="hitl-control-compact">
              <label>Assignment protection</label>
              <select
                value={batchAssignmentLock}
                onChange={(event) => setBatchAssignmentLock(event.target.value)}
                disabled={disabled}
              >
                <option value="NONE">No assignment lock</option>
                <option value="VEHICLE">Keep selected driver</option>
                <option value="FULL">Lock driver and sequence</option>
              </select>
            </div>
          )}
          <button type="button" className="hitl-primary-button hitl-apply-button" onClick={applyBatch} disabled={disabled}>
            Apply to selected stops
          </button>
        </div>
      </div>

      <div className="hitl-sequence-control">
        <label>Sequence protection after save</label>
        <select
          value={sequenceLock}
          onChange={(event) => setSequenceLock(event.target.value)}
          disabled={disabled}
        >
          <option value="NONE">NONE — allocator may reorder</option>
          <option value="RELATIVE">RELATIVE — preserve chosen relative order</option>
          <option value="FIXED">FIXED — preserve exact stop positions</option>
        </select>
      </div>

      <div className="hitl-stop-list">
        {stops.map((stop, index) => {
          const assignment = assignmentByBooking.get(Number(stop.booking_id));
          const driver = plannedDriver(assignment);
          const locked = isStopLocked(stop) || assignment?.locked;
          const selected = selectedStopIds.includes(String(stop.stop_id));
          return (
            <div
              key={stop.stop_id || stop.id}
              className={`hitl-stop-plan-row${selected ? " is-selected" : ""}${locked ? " is-locked" : ""}`}
            >
              <div className="hitl-stop-select">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleStop(stop)}
                  disabled={disabled || locked}
                  aria-label={`Select ${stop.type} for booking ${stop.booking_id}`}
                />
              </div>
              <div className="hitl-stop-order">
                <span>{index + 1}</span>
                <button type="button" onClick={() => moveStop(index, -1)} disabled={disabled || locked || index === 0}>
                  <ArrowUp size={16} />
                </button>
                <button type="button" onClick={() => moveStop(index, 1)} disabled={disabled || locked || index === stops.length - 1}>
                  <ArrowDown size={16} />
                </button>
              </div>
              <div className="hitl-stop-main">
                <div className="hitl-stop-heading">
                  <span className={`hitl-stop-type ${String(stop.type).toLowerCase()}`}>
                    {String(stop.type).toUpperCase()}
                  </span>
                  <strong>Booking #{stop.booking_id}</strong>
                  {stop.customer_name && <span>{stop.customer_name}</span>}
                </div>
                <div className="hitl-stop-address">
                  <MapPin size={16} /> {stop.location_address || "Location unavailable"}
                </div>
              </div>
              <div className="hitl-planned-driver">
                <span className="hitl-planned-label">Planned assignment</span>
                {!assignment?.targetVehicleId ? (
                  <strong className="hitl-unassigned-label">Return to Trips</strong>
                ) : (
                  <>
                    <span><UserRound size={15} /> {driver?.driver_name || "Unnamed driver"}</span>
                    <span><Phone size={15} /> {driver?.mobile_number || "No phone number"}</span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StopAssignmentPlanner;
